<template>
  <v-card outlined class="charging-point-configuration-changes">
    <v-card-subtitle>{{ $t('charging-point-configuration-changes.explained') }}</v-card-subtitle>
    <v-card-text class="d-flex align-center flex-wrap pb-0" style="gap: 12px">
      <v-select v-model="origin" :items="originItems" :label="$t('charging-point-configuration-changes.origin')" outlined dense hide-details style="max-width: 240px" />
      <v-text-field v-model="byName" :label="$t('charging-point-configuration-changes.author')" outlined dense hide-details clearable style="max-width: 240px" />
      <v-select v-model="days" :items="dayItems" :label="$t('charging-point-configuration-changes.period')" outlined dense hide-details style="max-width: 200px" />
      <v-spacer />
      <v-btn small text :disabled="!total" :loading="exporting" @click="exportCsv"><v-icon small left>mdi-download</v-icon>CSV</v-btn>
    </v-card-text>
    <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{'items-per-page-options': [50, 100, 200]}"
        :no-data-text="$t('charging-point-configuration-changes.none')"
        dense
    >
      <template #item.observedAt="{item}">{{ formatDate(item.observedAt, 'DD/MM/YYYY HH:mm') }}</template>
      <template #item.name="{item}">
        <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.name || item.identifier || item.chargingPointId }}</router-link>
        <div class="text-caption text--secondary">{{ [item.organizationName, item.locationName].filter(v => v).join(' · ') }}</div>
      </template>
      <template #item.key="{item}"><span class="mono">{{ item.key }}</span></template>
      <template #item.change="{item}">
        <span class="mono">
          <template v-if="item.sensitive">{{ $t('charging-point-configuration.secret-changed') }}</template>
          <template v-else><span class="text--secondary">{{ item.appeared ? $t('charging-point-configuration.absent') : display(item.before) }}</span> → {{ item.disappeared ? $t('charging-point-configuration.absent') : display(item.after) }}</template>
        </span>
      </template>
      <template #item.origin="{item}">
        <v-chip x-small :color="ORIGIN_COLORS[item.origin]" text-color="white">{{ $t('charging-point-configuration.origins.' + item.origin) }}</v-chip>
        <div v-if="item.byName || item.firmwareVersion" class="text-caption text--secondary">{{ item.byName || item.firmwareVersion }}</div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import {downloadCsv} from "@includes/csv";

/**
 * Les changements de configuration constatés sur le parc (le périmètre filtré) : hors supervision par défaut, ou faits
 * par nous (et par qui), ou par un firmware ; sur une période ; exportables.
 */
export default {
  props: {
    filters: {type: Object, default: () => ({})},
  },

  data() {
    return {
      origin: 'external',
      byName: '',
      days: 30,
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      options: {page: 1, itemsPerPage: 50},
      ORIGIN_COLORS: {ours: 'primary', firmware: 'purple', external: 'orange'},
    }
  },

  computed: {
    originItems() {
      return [
        {value: null, text: this.$t('charging-point-configuration-changes.all-origins')},
        ...['external', 'ours', 'firmware'].map(origin => ({value: origin, text: this.$t('charging-point-configuration.origins.' + origin)})),
      ]
    },
    dayItems() {
      return [7, 30, 90, 365].map(days => ({value: days, text: this.$tc('charging-point-configuration-changes.days', days, {count: days})}))
    },
    criteria() {
      return {origin: this.origin, byName: this.byName || null, days: this.days}
    },
    headers() {
      return [
        {text: this.$t('charging-point-fleet-configuration.seen-at'), value: 'observedAt', width: 150, sortable: false},
        {text: this.$t('charging-point-fleet-configuration.charging-point'), value: 'name', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.key'), value: 'key', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.change'), value: 'change', sortable: false},
        {text: this.$t('charging-point-configuration-changes.origin'), value: 'origin', sortable: false},
      ]
    },
  },

  watch: {
    filters: {
      handler() {
        this.restart()
      },
      deep: true,
    },
    criteria: {
      handler() {
        this.restart()
      },
      deep: true,
    },
    options: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  methods: {
    // Une page de plus haut : le changement d'options relance la liste.
    restart() {
      this.options = {...this.options, page: 1}
    },

    load() {
      const {page, itemsPerPage} = this.options
      this.loading = true
      ChargingPointConfigurationRepository.changes(this.filters, {...this.criteria, offset: (page - 1) * itemsPerPage, limit: itemsPerPage}).then(({total, rows}) => {
        this.total = total
        this.rows = rows
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    async exportCsv() {
      this.exporting = true
      try {
        const rows = []
        for (let offset = 0; offset < this.total; offset += 500) {
          rows.push(...(await ChargingPointConfigurationRepository.changes(this.filters, {...this.criteria, offset, limit: 500})).rows)
        }
        downloadCsv('changements-configuration.csv', [
          {text: this.$t('charging-point-fleet-configuration.seen-at'), value: 'observedAt'},
          {text: this.$t('charging-point-fleet-configuration.charging-point'), value: row => row.name || row.identifier || row.chargingPointId},
          {text: this.$t('charging-point-fleet-configuration.organization'), value: 'organizationName'},
          {text: this.$t('charging-point-fleet-configuration.key'), value: 'key'},
          {text: this.$t('charging-point-configuration-changes.before'), value: row => row.sensitive ? '' : row.before},
          {text: this.$t('charging-point-configuration-changes.after'), value: row => row.sensitive ? '' : row.after},
          {text: this.$t('charging-point-configuration-changes.origin'), value: row => this.$t('charging-point-configuration.origins.' + row.origin)},
          {text: this.$t('charging-point-configuration-changes.author'), value: 'byName'},
        ], rows)
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.exporting = false
      }
    },

    display(value) {
      return value === null || value === '' ? '∅' : value
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
</style>
